import {
  APP_NAME,
  CATEGORY_LABELS,
  CHANNEL_VALUE_DRIVERS,
  CLIENT_TYPE_LABELS,
  COMMERCIAL_TERMS,
  MARKET_LABELS,
  PROFILE_LABELS
} from '../data/knowledgeBase';
import {
  MANUAL_DECOA_MANDATES,
  MANUAL_MARKET_READING_QUESTIONS,
  MANUAL_ABSORPTION_NOTE,
  MANUAL_EXTERNAL_MARKET_PILLARS,
  MANUAL_INTERNAL_MARKET_PILLARS,
  MANUAL_STRATEGIC_PILLARS
} from '../data/manualBase';
import { SCENARIO_ARCHETYPES } from '../data/scenarioArchetypes';
import {
  FCC_CATEGORY_PLAYBOOKS,
  FCC_CHANNEL_PLAYBOOKS,
  FCC_META_ANCHORS,
  FCC_METHOD_ANCHORS,
  FCC_MPDV_ANCHORS,
  FCC_TOOLS_BY_MARKET
} from '../data/fccPlaybooks';
import { CATEGORY_PRODUCT_SUGGESTIONS, EXECUTION_BY_MARKET, SCENARIO_SIGNAL_LIBRARY } from '../data/productPlaybooks';
import type {
  ClientType,
  CustomerBehaviorProfile,
  EpaBlock,
  FoodCategory,
  Market,
  OpcBlock,
  PreparationInput,
  PreparationResult,
  SpinBlock,
  ValueSineMap,
  ValueSineStep
} from '../types';
import { normalizeText, unique } from '../utils/text';

type GoalKey =
  | 'defender_preco'
  | 'abrir_espaco'
  | 'ampliar_mix'
  | 'aumentar_share'
  | 'melhorar_execucao'
  | 'ganhar_exposicao'
  | 'construir_plano'
  | 'introduzir_sku'
  | 'fortalecer_parceria';

interface ProfilePlaybook {
  titulo: string;
  linguagem: string[];
  postura: string[];
  ritmo: string[];
  fechamento: string[];
  objectiveLead: string;
  processLead: string;
  commitmentLead: string;
  epaTone: string;
  spinOpening: string;
  epaOpening: string;
  opcOpening: string;
}

interface ScenarioReading {
  summary: string;
  archetypeLabel: string;
  archetypeFocus: string;
  archetypeOpcAngle: string;
  archetypeSpinAngle: string;
  archetypeEpaAngle: string;
  archetypeSineAngle: string;
  goal: GoalKey;
  goalLabel: string;
  corePain: string;
  commercialRisk: string;
  nextStep: string;
  channelFocus: string;
  valueDrivers: string[];
  channelMetrics: string[];
  counterpartyIdeas: string[];
  routineAnchors: string[];
  categoryArguments: string[];
  scenarioSignals: string[];
  actionFocus: string;
  suggestedCounterparty: string;
  tags: string[];
}

const PROFILE_PLAYBOOKS: Record<CustomerBehaviorProfile, ProfilePlaybook> = {
  analitico: {
    titulo: 'Condução lógica, estruturada e baseada em fatos comerciais',
    linguagem: [
      'Use comparativos, histórico, margem, giro, entrada de pedidos e saída da categoria para dar lógica à conversa.',
      'Mostre conexão entre mix, performance da categoria e resultado financeiro sem apelar para pressão emocional.'
    ],
    postura: [
      'Entre com método, organize as hipóteses e faça o cliente enxergar a lógica econômica antes de falar em condição comercial.',
      'Dê segurança ao cliente com clareza de critério, consistência e sequência.'
    ],
    ritmo: [
      'Conduza em ordem: cenário atual, desvio de performance, impacto quantitativo e próximo passo.',
      'Aprofunde problema e implicação com foco em números, não em opinião.'
    ],
    fechamento: [
      'Feche com compromisso objetivo, indicador de sucesso e contrapartida clara quando houver pedido de preço ou investimento.'
    ],
    objectiveLead: 'enquadre a conversa com lógica comercial clara',
    processLead: 'aprofundar a leitura por causa e efeito',
    commitmentLead: 'formalize o próximo passo com critério mensurável',
    epaTone: 'credibilidade técnica e comercial',
    spinOpening: 'Comece com perguntas estruturadas e ancoradas em fatos, para o cliente organizar o raciocínio com você.',
    epaOpening: 'Eduque com números, comparativos e critério econômico.',
    opcOpening: 'Defina o rumo da conversa com método e sequência lógica.'
  },
  diretivo: {
    titulo: 'Condução direta, curta e orientada a decisão',
    linguagem: [
      'Use frases curtas, foco em resultado, margem, giro, velocidade e decisão.',
      'Corte rodeios e leve a conversa cedo para impacto direto no negócio.'
    ],
    postura: [
      'Mostre onde a conta está ganhando ou perdendo resultado e qual decisão destrava isso.',
      'Se houver pedido de preço, retome valor e contrapartida sem alongar demais a discussão.'
    ],
    ritmo: [
      'Faça perguntas pragmáticas e empilhe pouco conteúdo por vez.',
      'Priorize o que move decisão agora e deixe o resto como apoio.'
    ],
    fechamento: [
      'Feche com um combinado simples, rápido e com dono definido.'
    ],
    objectiveLead: 'defina rápido o que precisa ser conquistado',
    processLead: 'leve a conversa para o ponto que destrava resultado',
    commitmentLead: 'converta a reunião em decisão ou teste objetivo',
    epaTone: 'impacto direto e clareza de decisão',
    spinOpening: 'Faça perguntas curtas e pragmáticas que acelerem a leitura do problema.',
    epaOpening: 'Eduque com impacto direto no resultado e na velocidade da decisão.',
    opcOpening: 'Defina um rumo simples, direto e sem desvio.'
  },
  integrador: {
    titulo: 'Condução empática, segura e construída em parceria',
    linguagem: [
      'Use tom acolhedor, respeitoso e conversacional, sem perder firmeza comercial.',
      'Valorize confiança, previsibilidade, apoio e construção conjunta.'
    ],
    postura: [
      'Mostre que a C.Vale quer crescer junto, não empurrar condição ou volume.',
      'Valide entendimento durante a conversa e use o cliente como coautor do próximo passo.'
    ],
    ritmo: [
      'Crie espaço para o cliente explicar o dia a dia, a operação e as preocupações da conta.',
      'Use o SPIN para aprofundar de forma natural, não interrogativa.'
    ],
    fechamento: [
      'Feche com um compromisso confortável, claro e acompanhado de apoio.'
    ],
    objectiveLead: 'construa alinhamento com segurança e respeito',
    processLead: 'conectar escuta, leitura de canal e parceria comercial',
    commitmentLead: 'ajuste o próximo passo junto com o cliente',
    epaTone: 'confiança, apoio e construção conjunta',
    spinOpening: 'Faça perguntas que convidem o cliente a construir a resposta com você, sem pressão.',
    epaOpening: 'Eduque acolhendo, conectando a proposta à segurança e à parceria.',
    opcOpening: 'Defina o rumo com espaço para construção conjunta do compromisso.'
  },
  expressivo: {
    titulo: 'Condução envolvente, energética e voltada a oportunidade',
    linguagem: [
      'Use linguagem viva, inspiradora e orientada a crescimento, diferenciação e visão de futuro.',
      'Traga energia sem exagero e conecte valor a conquista percebida.'
    ],
    postura: [
      'Mostre a oportunidade escondida no cenário e pinte a imagem de um resultado melhor para a categoria.',
      'Use storytelling com equilíbrio, sempre ancorado em lógica comercial.'
    ],
    ritmo: [
      'Faça perguntas abertas e provocativas para ampliar a ambição do cliente.',
      'Leve a conversa de situação para oportunidade com sensação de movimento.'
    ],
    fechamento: [
      'Feche com impulso para ação, visão de ganho e um próximo passo que gere tração.'
    ],
    objectiveLead: 'transforme a conversa em oportunidade concreta',
    processLead: 'provocar visão, diferenciação e crescimento',
    commitmentLead: 'gerar avanço com energia e direcionamento',
    epaTone: 'conexão emocional com visão de negócio',
    spinOpening: 'Abra perguntas amplas e provocativas para fazer o cliente enxergar oportunidade onde ele via rotina.',
    epaOpening: 'Eduque trazendo visão de crescimento, diferenciação e futuro.',
    opcOpening: 'Defina o rumo como uma oportunidade de avanço, não só como correção.'
  }
};

const GOAL_RULES: Array<{ key: GoalKey; label: string; regex: RegExp }> = [
  { key: 'defender_preco', label: 'defender preço com valor', regex: /preco|desconto|verba|margem pressionada|guerra de preco|baratear/ },
  { key: 'abrir_espaco', label: 'abrir espaço comercial na conta', regex: /abrir espaco|entrar na rede|novo fornecedor|ganhar cadastro|abrir porta/ },
  { key: 'ampliar_mix', label: 'ampliar mix da categoria', regex: /mix|sortimento|mais itens|linhas|portfolio/ },
  { key: 'aumentar_share', label: 'aumentar participação e relevância na conta', regex: /share|participacao|ganhar espaço do concorrente|aumentar presenca/ },
  { key: 'melhorar_execucao', label: 'melhorar execução comercial', regex: /execucao|ruptura|abastecimento|reposicao|ponta|gondola|layout/ },
  { key: 'ganhar_exposicao', label: 'ganhar exposição qualificada', regex: /exposicao|gondola|ilha|ponta|visibilidade/ },
  { key: 'construir_plano', label: 'construir plano conjunto', regex: /plano|jbp|calendario|crescimento|estrategia|projeto/ },
  { key: 'introduzir_sku', label: 'introduzir novo SKU', regex: /sku|lancamento|introduzir|novo item|novo produto/ },
  { key: 'fortalecer_parceria', label: 'fortalecer parceria comercial', regex: /parceria|relacionamento|renovar|longo prazo|fidelizar/ }
];

const PAIN_RULES: Array<{ regex: RegExp; value: string }> = [
  { regex: /ruptura|falta|abastecimento/, value: 'a conta corre risco de perder venda e confiança por falta de regularidade no abastecimento.' },
  { regex: /preco|desconto|barato/, value: 'a negociação está escorregando para preço puro e ameaça margem sem capturar contrapartida real.' },
  { regex: /mix|sortimento/, value: 'o mix atual parece subaproveitar a categoria e limitar giro, margem ou diferenciação.' },
  { regex: /giro|sell-out/, value: 'o cliente precisa melhorar rotação e transformar a compra em saída real da categoria.' },
  { regex: /share|concorrente/, value: 'a C.Vale pode estar perdendo espaço estratégico por falta de narrativa de valor mais forte.' },
  { regex: /execucao|exposicao|gondola/, value: 'a execução no ponto de venda não está sustentando o resultado que a categoria pode entregar.' },
  { regex: /embarque|export|container|document/, value: 'a conta precisa previsibilidade operacional e segurança para manter continuidade comercial.' }
];

const RISK_BY_MARKET: Record<Market, string> = {
  interno: 'A conversa virar só discussão de preço e deixar margem, giro, mix e execução fora da mesa.',
  externo: 'A conversa reduzir valor a preço FOB e ignorar previsibilidade, conformidade, risco operacional e custo de troca de fornecedor.'
};

const CATEGORY_CONTEXT: Record<FoodCategory, { narrative: string; risk: string }> = {
  aves: {
    narrative: 'uma categoria puxada por regularidade, padrão e equilíbrio entre giro e rentabilidade',
    risk: 'desbalancear preço e volume sem proteger padrão, abastecimento e margem'
  },
  embutidos: {
    narrative: 'uma categoria de valor agregado em que exposição, mix e diferenciação pesam muito',
    risk: 'queimar margem por desconto sem construir valor percebido na gôndola'
  },
  industrializados: {
    narrative: 'uma categoria onde estratégia, sortimento e calendário comercial mudam a performance',
    risk: 'entrar em promoção rasa sem gerar crescimento sustentável'
  },
  pescados: {
    narrative: 'uma categoria que depende de confiança, qualidade certificada e previsibilidade',
    risk: 'perder confiança por falha de fornecimento, padrão ou ruptura'
  }
};

const MARKET_TERMS: Record<Market, string[]> = {
  interno: ['giro', 'margem', 'entrada de pedidos', 'saída da categoria', 'exposição', 'ruptura', 'mix', 'participação', 'rentabilidade da categoria'],
  externo: ['regularidade de fornecimento', 'qualidade certificada', 'rastreabilidade', 'padrão', 'previsibilidade', 'segurança de fornecimento']
};

const labelList = (items: string[]): string => {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} e ${items[1]}`;
  return `${items.slice(0, -1).join(', ')} e ${items[items.length - 1]}`;
};

const detectGoal = (normalized: string): { key: GoalKey; label: string } => {
  const found = GOAL_RULES.find((rule) => rule.regex.test(normalized));
  return found ?? { key: 'construir_plano', label: 'construir um plano de valor para a conta' };
};

const detectPain = (normalized: string, market: Market, category: FoodCategory): string => {
  const found = PAIN_RULES.find((rule) => rule.regex.test(normalized));
  if (found) return found.value;
  return `o cliente ainda não está capturando todo o valor de ${CATEGORY_CONTEXT[category].narrative} e isso pode pressionar ${market === 'interno' ? 'margem, giro e execução' : 'previsibilidade, padrão e continuidade comercial'}.`;
};

const buildTags = (scenario: string, market: Market, category: FoodCategory, clientType: ClientType): string[] => {
  const normalized = normalizeText(scenario);
  const dynamicTags = COMMERCIAL_TERMS.filter((term) => normalizeText(term).split(' ').some((part) => normalized.includes(part)));
  return unique([MARKET_LABELS[market], CATEGORY_LABELS[category], CLIENT_TYPE_LABELS[clientType], ...dynamicTags]).slice(0, 8);
};

const detectScenarioSignals = (normalized: string): string[] =>
  SCENARIO_SIGNAL_LIBRARY.filter((signal) => signal.regex.test(normalized)).map((signal) => signal.key);

const selectActionFocus = (signals: string[], goalLabel: string): string => {
  const matched = SCENARIO_SIGNAL_LIBRARY.find((signal) => signals.includes(signal.key));
  return matched?.actionLabel ?? goalLabel;
};

const selectCounterparty = (signals: string[], ideas: string[], market: Market): string => {
  if (signals.includes('exposicao')) return labelList(ideas.filter((item) => /encarte|ponto extra|degust|espaco/.test(normalizeText(item))).slice(0, 2)) || ideas[0];
  if (signals.includes('mix')) return labelList(ideas.filter((item) => /mix|sku|volume/.test(normalizeText(item))).slice(0, 2)) || ideas[0];
  if (signals.includes('logistica')) return labelList(ideas.filter((item) => /vigencia|calend|volume/.test(normalizeText(item))).slice(0, 2)) || ideas[0];
  if (signals.includes('preco')) return market === 'interno' ? 'volume incremental e contrapartida de execução' : 'vigência de preço e volume mínimo';
  return ideas[0];
};

const detectArchetype = (normalized: string, market: Market, goal: GoalKey) =>
  SCENARIO_ARCHETYPES.find((archetype) => {
    if (archetype.market && archetype.market !== market) return false;
    const regexMatch = archetype.regexes.every((regex) => regex.test(normalized));
    if (!regexMatch) return false;
    if (!archetype.goalBias?.length) return true;
    return archetype.goalBias.includes(goal);
  }) ?? null;

const rankProductSuggestions = (
  normalized: string,
  category: FoodCategory,
  goal: GoalKey,
  signals: string[]
) => {
  const products = CATEGORY_PRODUCT_SUGGESTIONS[category];
  return [...products]
    .map((product) => {
      let score = 0;
      score += product.goalFit?.includes(goal) ? 4 : 0;
      score += product.scenarioSignals?.filter((signal) => signals.includes(signal)).length ?? 0;
      score += product.scenarioSignals?.filter((signal) => normalized.includes(normalizeText(signal))).length ?? 0;
      score += product.papel === 'lider' && signals.includes('ruptura') ? 2 : 0;
      score += product.papel === 'margem' && (signals.includes('preco') || signals.includes('premium')) ? 2 : 0;
      score += product.papel === 'complementar' && (signals.includes('mix') || signals.includes('exposicao')) ? 2 : 0;
      return { product, score };
    })
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.product);
};

const buildScenarioReading = (input: PreparationInput): ScenarioReading => {
  const normalized = normalizeText(input.scenario);
  const goal = detectGoal(normalized);
  const scenarioSignals = detectScenarioSignals(normalized);
  const archetype = detectArchetype(normalized, input.market, goal.key);
  const channelPlaybook = FCC_CHANNEL_PLAYBOOKS[input.clientType];
  const categoryPlaybook = FCC_CATEGORY_PLAYBOOKS[input.category];
  const drivers = unique([
    ...CHANNEL_VALUE_DRIVERS[input.clientType],
    ...channelPlaybook.focus,
    ...MARKET_TERMS[input.market],
    ...categoryPlaybook.proofPoints,
    CATEGORY_CONTEXT[input.category].narrative
  ]).slice(0, 6);
  const routineAnchors = unique([
    ...FCC_META_ANCHORS,
    ...FCC_METHOD_ANCHORS.slice(0, 3),
    ...MANUAL_MARKET_READING_QUESTIONS.slice(0, 2).map((item) => `Pergunta de preparo: ${item}`),
    ...(input.market === 'interno' ? FCC_MPDV_ANCHORS.slice(0, 3) : ['Preço se defende com valor comprovado, não com desconto.', 'No mercado externo, concessão sem contrapartida vira precedente.']),
    ...FCC_TOOLS_BY_MARKET[input.market].map((tool) => `Usar ${tool} como suporte da argumentação.`)
  ]).slice(0, 6);

  return {
    summary: `Conta de ${CLIENT_TYPE_LABELS[input.clientType].toLowerCase()} no ${MARKET_LABELS[input.market].toLowerCase()}, dentro da categoria ${CATEGORY_LABELS[input.category].toLowerCase()}, com cenário que pede ${goal.label}. ${archetype ? `Este caso se parece com o arquétipo "${archetype.label.toLowerCase()}", então a recomendação prioriza ${archetype.focus}.` : 'A lógica FCC aqui é proteger margem, sustentar valor e avançar com contrapartida e método.'}`,
    archetypeLabel: archetype?.label ?? 'Cenário sem arquétipo dominante',
    archetypeFocus: archetype?.focus ?? goal.label,
    archetypeOpcAngle: archetype?.opcAngle ?? 'organizar objetivo, processo e compromisso com clareza comercial',
    archetypeSpinAngle: archetype?.spinAngle ?? 'aprofundar a dor real e separar sintoma de causa',
    archetypeEpaAngle: archetype?.epaAngle ?? 'reposicionar o critério de decisão para valor e próximo passo',
    archetypeSineAngle: archetype?.sineAngle ?? 'levar a conversa do contexto até a solução com lógica de valor',
    goal: goal.key,
    goalLabel: goal.label,
    corePain: detectPain(normalized, input.market, input.category),
    commercialRisk: RISK_BY_MARKET[input.market],
    nextStep:
      input.market === 'interno'
        ? `${channelPlaybook.nextStep} Registre no CRM/FV e programe retorno em +1, +3 e +7 dias.`
        : `${channelPlaybook.nextStep} Formalize com vigência, volume, calendário e registro completo da negociação.`,
    channelFocus: channelPlaybook.focus[0] ?? CHANNEL_VALUE_DRIVERS[input.clientType][0],
    valueDrivers: drivers,
    channelMetrics: channelPlaybook.metrics,
    counterpartyIdeas: channelPlaybook.counterpartyIdeas,
    routineAnchors,
    categoryArguments: unique([categoryPlaybook.architecture, ...categoryPlaybook.cvb, `Estratégia de mix ideal: ${labelList(categoryPlaybook.mixLogic)}.`]).slice(0, 4),
    scenarioSignals,
    actionFocus: selectActionFocus(scenarioSignals, goal.label),
    suggestedCounterparty: selectCounterparty(scenarioSignals, channelPlaybook.counterpartyIdeas, input.market),
    tags: buildTags(input.scenario, input.market, input.category, input.clientType)
  };
};

const buildObjective = (reading: ScenarioReading, input: PreparationInput): string[] => [
  `O foco da reunião é ${reading.goalLabel}, sem cair em desconto solto ou conversa fraca de preço.`,
  `Comece com direção clara e mostre onde está o ganho para ${CLIENT_TYPE_LABELS[input.clientType].toLowerCase()}.`,
  `Mostre como ${labelList(reading.valueDrivers.slice(0, 3))} sustentam resultado de verdade.`,
  `A meta é fazer o cliente enxergar que ${reading.corePain.replace(/^a conta /, '')}`,
  input.market === 'interno'
    ? 'Defenda PPC e PPV com lógica de giro, margem e execução no ponto de venda.'
    : 'Defenda preço com base em regularidade, conformidade e custo de troca de fornecedor.'
];

const buildProcess = (reading: ScenarioReading, input: PreparationInput): string[] => [
  'Comece entendendo como a conta compra hoje, o papel da categoria e onde está o maior aperto.',
  input.market === 'interno'
    ? 'Entre com leitura de loja, preço conferido, margem e um pré-pedido simples em mãos.'
    : 'Entre com histórico de embarques, risco logístico e proposta de volume e calendário pronta.',
  `Depois, conecte o dia a dia do cliente a impactos em ${labelList(reading.valueDrivers.slice(0, 4))}.`,
  `Use a realidade de ${CATEGORY_LABELS[input.category].toLowerCase()} para tirar a conversa da comparação rasa.`,
  'Feche mostrando o próximo passo e o valor da parceria com a C.Vale.'
];

const buildCommitment = (reading: ScenarioReading, input: PreparationInput): string[] => [
  `O próximo passo ideal é ${reading.nextStep}`,
  `Se houver pedido de condição comercial, amarre qualquer ajuste a contrapartida de ${labelList(reading.counterpartyIdeas.slice(0, 3))}.`,
  input.market === 'interno'
    ? 'Saia com um combinado que possa ser medido em giro, margem, exposição, saída da categoria ou participação.'
    : 'Busque um combinado que permita medir resultado em previsibilidade, padrão, conformidade, calendário de embarque ou continuidade do fornecimento.'
];

const buildSpin = (reading: ScenarioReading, input: PreparationInput): SpinBlock => ({
  situacao: [
    `Como vocês estão conduzindo hoje a categoria de ${CATEGORY_LABELS[input.category].toLowerCase()} dentro dessa conta e quais critérios mais pesam na decisão entre fornecedor, mix e preço?`,
    `Quando vocês olham para ${CLIENT_TYPE_LABELS[input.clientType].toLowerCase()}, o que hoje tem mais peso: ${labelList(reading.channelMetrics.slice(0, 3))} ou construção de parceria?`,
    input.market === 'interno'
      ? 'Como vocês acompanham hoje entrada de pedidos, saída da categoria, ruptura, exposição, PPC, PPV e rentabilidade para decidir onde avançar ou recuar?'
      : 'Como vocês avaliam hoje padrão, rastreabilidade, regularidade de fornecimento e segurança operacional para manter a conta estável?',
    input.market === 'interno'
      ? 'Hoje, onde a categoria está mais sensível para vocês: composição de mix, execução no PDV, preço na gôndola ou previsibilidade de abastecimento?'
      : 'Hoje, onde a operação fica mais sensível: calendário de embarque, estabilidade de padrão, documentação ou previsibilidade de fornecimento?'
  ],
  problema: [
    `Onde o modelo atual deixa a categoria mais vulnerável e impede a conta de capturar melhor ${labelList(reading.valueDrivers.slice(0, 3))}?`,
    input.market === 'interno'
      ? 'O que mais trava hoje resultado na ponta: preço mal posicionado, mix pouco produtivo, execução fraca, ruptura ou falta de plano comercial?'
      : 'O que mais trava hoje a evolução da parceria: oscilação de padrão, risco de abastecimento, previsibilidade limitada ou pressão por troca de fornecedor?',
    `Quando surge pressão por negociação, em que momento vocês sentem que a discussão desvia do que realmente sustenta resultado para a conta?`,
    input.market === 'interno'
      ? `Quando vocês tentam reagir a isso hoje, o que continua sem resolver: margem, giro, exposição, ruptura ou rentabilidade da categoria?`
      : `Quando vocês tentam resolver isso hoje, o que continua exposto: custo de troca, risco operacional, estabilidade do fornecimento ou confiança comercial?`
  ],
  implicacao: [
    input.market === 'interno'
      ? 'Se isso continuar assim, quanto a conta pode deixar na mesa em margem, giro, participação, faturamento ou rentabilidade da categoria?'
      : 'Se isso continuar assim, qual é o custo real em previsibilidade, segurança de fornecimento, conformidade e confiança comercial?',
    `Quando a decisão fica presa no curto prazo, que efeito isso gera sobre ${labelList(reading.valueDrivers.slice(0, 3))} ao longo dos próximos ciclos?`,
    input.market === 'interno'
      ? 'Quanto uma ruptura, um markdown mal calibrado ou um mix abaixo do potencial pesa no resultado total da conta?'
      : 'Quanto uma troca de fornecedor, um embarque inconsistente ou uma variação de padrão pode custar em continuidade e competitividade?'
  ],
  necessidadeSolucao: [
    `O que vocês precisariam enxergar para avançar com segurança para uma proposta que proteja valor e ao mesmo tempo entregue resultado para ${CLIENT_TYPE_LABELS[input.clientType].toLowerCase()}?`,
    input.market === 'interno'
      ? 'Que tipo de plano faria sentido para vocês: revisão de mix, agenda de execução, contrapartida por condição comercial ou meta conjunta de saída da categoria?'
      : 'Que tipo de construção faria sentido para vocês: calendário mais previsível, padrão assegurado, redução de risco operacional ou plano de continuidade de fornecimento?',
    `Se a C.Vale estruturar isso de forma prática, qual seria o próximo passo mais coerente para testar ou escalar essa proposta?`
  ]
});

const buildEpa = (reading: ScenarioReading, input: PreparationInput, profile: ProfilePlaybook): EpaBlock => ({
  educar: [
    profile.epaOpening,
    `Mostre que preço sozinho não resolve, porque ${labelList(reading.valueDrivers.slice(0, 3))} mexem mais no resultado da conta.`,
    input.market === 'interno'
      ? 'Compare desconto com resultado da categoria: mix, execução, PPV certo e saída sustentam melhor a margem.'
      : 'Compare preço unitário com custo total: previsibilidade, padrão e conformidade reduzem risco.',
    `Leve o cliente a rever o critério atual e olhar mais para valor do que para concessão.`
  ],
  personalizar: [
    `Conecte a proposta à realidade de ${CLIENT_TYPE_LABELS[input.clientType].toLowerCase()}, usando ${CATEGORY_LABELS[input.category].toLowerCase()} de forma bem prática.`,
    reading.categoryArguments[0],
    input.market === 'interno'
      ? 'Traduza a proposta em ganho de giro, margem, exposição, participação ou rentabilidade.'
      : 'Traduza a proposta em segurança de fornecimento, estabilidade de padrão, rastreabilidade e previsibilidade operacional.',
    `Mostre como a parceria com a C.Vale ajuda a resolver ${reading.corePain.replace(/^a conta /, '')}`
  ],
  assumirControle: [
    'Conduza com firmeza e traga a conversa de volta para valor sempre que ela cair em preço puro.',
    input.market === 'interno'
      ? 'Leve a reunião para um próximo passo concreto com contrapartida clara de volume, mix, espaço, execução ou calendário.'
      : 'Reoriente a reunião para um próximo passo operacional e comercial concreto com critérios de previsibilidade, padrão ou continuidade.',
    'Mostre que avançar sem clareza de valor só empurra o problema para frente.'
  ]
});

const buildSineSteps = (reading: ScenarioReading, input: PreparationInput): ValueSineStep[] => [
  {
    id: 1,
    titulo: 'Estabelecer o cenário',
    resumo: 'Abra mostrando leitura real da conta e enquadre a conversa no que mais pesa para o cliente agora.',
    explicacao: `Mostre que você entendeu o momento de ${CLIENT_TYPE_LABELS[input.clientType].toLowerCase()} e o papel da categoria no negócio.`,
    guiaTatico: [
      'Retome o momento da conta com palavras simples e ligadas ao dia a dia da loja ou da operação.',
      'Valide o que pesa mais hoje: margem, giro, regularidade, exposição, participação ou previsibilidade.',
      'Faça o cliente sentir que a conversa será prática e útil.'
    ],
    sugestaoPersonalizada: `Abra pelo ponto que mais importa aqui: ${reading.channelFocus}.`
  },
  {
    id: 2,
    titulo: 'Reestruturar o problema',
    resumo: 'Dê nome certo à dor e tire a conversa do sintoma superficial.',
    explicacao: 'Saia do sintoma e mostre onde está a causa do problema.',
    guiaTatico: [
      'Aprofunde o problema com perguntas abertas e conectadas.',
      'Separe claramente dor atual de causa estrutural.',
      'Mostre que o problema não é apenas condição comercial.'
    ],
    sugestaoPersonalizada: `Ponto central da dor: ${reading.corePain}`
  },
  {
    id: 3,
    titulo: 'Impacto racional',
    resumo: 'Leve a dor para consequência de negócio com números, perda ou risco mensurável.',
    explicacao: 'Mostre o custo de manter tudo como está.',
    guiaTatico: [
      'Puxe margem, giro, participação, ruptura, estoque, saída da categoria ou previsibilidade conforme o mercado.',
      'Quando possível, provoque quantificação.',
      'Mostre perda escondida, não apenas perda visível.'
    ],
    sugestaoPersonalizada: `Use como eixo racional ${labelList(reading.valueDrivers.slice(0, 3))}.`
  },
  {
    id: 4,
    titulo: 'Impacto emocional',
    resumo: 'Conecte a dor à pressão do dia a dia, à insegurança da decisão e ao desgaste de continuar assim.',
    explicacao: 'Faça o cliente perceber o desgaste de seguir do jeito atual.',
    guiaTatico: [
      'Humanize a consequência sem dramatizar.',
      'Conecte risco comercial a tranquilidade, confiança e reputação da conta.',
      'Mostre o incômodo de continuar apagando incêndio.'
    ],
    sugestaoPersonalizada: input.market === 'interno' ? 'Traga o peso de negociar sempre na urgência.' : 'Traga o peso de expor a operação a instabilidade e surpresa.'
  },
  {
    id: 5,
    titulo: 'Proposta de valor',
    resumo: 'Mostre um novo critério de decisão e o caminho prático de avanço para a conta.',
    explicacao: 'Mostre a C.Vale como uma solução prática, ligada à rotina e ao resultado da conta.',
    guiaTatico: [
      'Mostre a nova régua de decisão.',
      'Conecte proposta a ganho real e mensurável.',
      'Faça o cliente verbalizar por que a solução faz sentido.'
    ],
    sugestaoPersonalizada: `Amarre valor em ${labelList(reading.valueDrivers.slice(0, 4))}.`
  },
  {
    id: 6,
    titulo: 'Apresentação das soluções C.Vale',
    resumo: 'Converta valor em próximo passo comercial claro, com compromisso e critério de avanço.',
    explicacao: 'Feche mostrando como a C.Vale entra com solução prática, apoio e clareza no próximo passo.',
    guiaTatico: [
      'Traduza a proposta em um próximo passo concreto.',
      'Negocie contrapartida quando houver pressão por condição.',
      'Feche com clareza, responsabilidade e cadência.'
    ],
    sugestaoPersonalizada: reading.nextStep
  }
];

const buildValueSineMap = (reading: ScenarioReading, input: PreparationInput): ValueSineMap => ({
  titulo: 'Senoide da Venda de Valor',
  subtitulo: `Narrativa recomendada para ${CLIENT_TYPE_LABELS[input.clientType].toLowerCase()} em ${MARKET_LABELS[input.market].toLowerCase()}, usando a categoria ${CATEGORY_LABELS[input.category].toLowerCase()} como argumento de valor. Nesta situação, a curva deve ${reading.archetypeSineAngle}.`,
  steps: buildSineSteps(reading, input),
  fases: [
    { nome: 'Gancho', etapas: [1, 2] },
    { nome: 'Impacto', etapas: [3, 4] },
    { nome: 'Solução', etapas: [5, 6] }
  ]
});

const buildManualAnchors = (market: Market): string[] => [
  ...MANUAL_STRATEGIC_PILLARS.slice(0, 3),
  ...(market === 'interno' ? MANUAL_INTERNAL_MARKET_PILLARS.slice(0, 2) : MANUAL_EXTERNAL_MARKET_PILLARS.slice(0, 2)),
  ...MANUAL_DECOA_MANDATES.slice(0, 2),
  ...FCC_METHOD_ANCHORS.slice(0, 3),
  MANUAL_ABSORPTION_NOTE
];

export const generatePreparation = (input: PreparationInput): PreparationResult => {
  const profile = PROFILE_PLAYBOOKS[input.customerProfile];
  const reading = buildScenarioReading(input);
  const categoryPlaybook = FCC_CATEGORY_PLAYBOOKS[input.category];
  const executionPlaybook = EXECUTION_BY_MARKET[input.market];
  const rankedProducts = rankProductSuggestions(normalizeText(input.scenario), input.category, reading.goal, reading.scenarioSignals);
  const opc: OpcBlock = {
    objetivo: buildObjective(reading, input),
    processo: buildProcess(reading, input),
    compromisso: buildCommitment(reading, input)
  };
  const spin: SpinBlock = buildSpin(reading, input);
  const epa: EpaBlock = buildEpa(reading, input, profile);
  const valueSineMap = buildValueSineMap(reading, input);
  const generatedAt = new Date().toISOString();

  return {
    tituloFicha: `${APP_NAME} | Preparação da Reunião`,
    cenarioOriginal: input.scenario,
    contexto: {
      perfil: input.customerProfile,
      categoria: input.category,
      mercado: input.market,
      tipoCliente: input.clientType
    },
    leituraCenario: {
      resumoExecutivo: reading.summary,
      tipoCenario: reading.archetypeLabel,
      objetivoCentral: reading.goalLabel,
      dorCentral: reading.corePain,
      riscoComercial: reading.commercialRisk,
      alavancasValor: [...reading.valueDrivers, ...reading.categoryArguments].slice(0, 8),
      direcionadoresManual: buildManualAnchors(input.market)
    },
    abordagemPerfil: {
      perfil: input.customerProfile,
      titulo: profile.titulo,
      linguagem: profile.linguagem,
      postura: profile.postura,
      ritmo: profile.ritmo,
      fechamento: profile.fechamento
    },
    opc,
    spin,
    epa,
    valueSineMap,
    mixExecution: {
      estrategiaMix: `Trabalhe a categoria ${CATEGORY_LABELS[input.category].toLowerCase()} com a lógica FCC: ${categoryPlaybook.architecture} Monte a conversa com item líder + complementar + item de margem para defender rentabilidade sem empobrecer o giro.`,
      focoDaAcao: reading.actionFocus,
      contrapartidaSugerida: reading.suggestedCounterparty,
      racionalEscolha: [
        `Sinais lidos no cenário: ${reading.scenarioSignals.length ? labelList(reading.scenarioSignals) : 'sem sinal dominante, então o motor priorizou o objetivo central.'}`,
        `Objetivo priorizado: ${reading.goalLabel}.`,
        `Canal pede foco em ${labelList(reading.channelMetrics.slice(0, 3))}.`
      ],
      skusSugeridos: rankedProducts,
      prioridadesExecucao: executionPlaybook.priorities.map((item, index) =>
        index === 0 && reading.scenarioSignals.includes('exposicao') ? `${item} Dê prioridade máxima a esse ponto nesta reunião.` : item
      ),
      provasValor: [...executionPlaybook.proof, ...executionPlaybook.routine].slice(0, 6)
    },
    dicasPraticas: [
      `Use linguagem compatível com o perfil ${PROFILE_LABELS[input.customerProfile].toLowerCase()} sem descaracterizar a firmeza comercial.`,
      `Leve a conversa para ${reading.channelFocus} e conecte isso ao objetivo de ${reading.goalLabel}.`,
      ...reading.routineAnchors.slice(0, 2),
      input.market === 'interno'
        ? 'Não aceite discutir desconto isolado sem contrapartida clara de volume, mix, espaço, execução ou calendário.'
        : 'Não aceite reduzir a proposta a preço sem defender previsibilidade, padrão, conformidade e continuidade do fornecimento.'
    ],
    proximoPasso: reading.nextStep,
    metadata: {
      generatedAt,
      tags: reading.tags,
      customerProfile: input.customerProfile,
      category: input.category,
      market: input.market,
      clientType: input.clientType,
      centralGoal: reading.goalLabel,
      channelFocus: reading.channelFocus,
      scenarioSignals: reading.scenarioSignals,
      archetype: reading.archetypeLabel
    }
  };
};
